/**
 * Eén speelkaart. Vast per-chakra frame (kleur/accent), centrale art-zone
 * (placeholder tot fase 7), type-badge, kosten en tekst. Dim/grijs als
 * onspeelbaar of bevroren.
 */
import type { KaartInst } from '../engine/state';
import { t, type Taal } from '../i18n';
import { kaartNaam, kaartTekst } from '../data/content';
import { KaartArt } from './art/KaartArt';

const TYPE_KLEUR: Record<string, string> = {
  s: '#9ca3af', // steen — grijs
  o: '#ef9a9a', // orgaan — rozerood
  p: '#a7d8a7', // praktijk — groen
  e: '#f0c674', // element — goud
  m: '#b0a4e3', // mantra — paars
  k: '#7fd1de', // kleur — cyaan
};

interface Props {
  inst: KaartInst;
  kleur: string;
  accent: string;
  speelbaar: boolean;
  reden: string | null;
  bevroren: boolean;
  taal: Taal;
  onSpeel: (instId: string) => void;
}

export function KaartView({ inst, kleur, accent, speelbaar, reden, bevroren, taal, onSpeel }: Props) {
  const k = inst.kaart;
  const typeKleur = TYPE_KLEUR[k.type] ?? '#999';

  return (
    <button
      type="button"
      disabled={!speelbaar}
      title={reden ?? kaartTekst(k, taal)}
      onClick={() => onSpeel(inst.instId)}
      className={[
        'relative flex w-32 shrink-0 flex-col overflow-hidden rounded-xl border text-left transition',
        speelbaar ? 'cursor-pointer hover:-translate-y-2 hover:shadow-lg' : 'cursor-not-allowed opacity-50',
      ].join(' ')}
      style={{
        borderColor: accent,
        background: `linear-gradient(160deg, #14121c 0%, ${kleur}22 100%)`,
        boxShadow: speelbaar ? `0 0 0 1px ${kleur}55 inset` : undefined,
      }}
    >
      {/* kop: kosten + type */}
      <div className="flex items-center justify-between px-2 pt-2">
        <span
          className="flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold text-black"
          style={{ background: accent, fontFamily: 'JetBrains Mono, monospace' }}
        >
          {k.cost}
        </span>
        {k.meesterschap && (
          <span className="text-xs" style={{ color: accent }} title="Meesterschapskaart">★</span>
        )}
        <span className="rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide" style={{ background: `${typeKleur}33`, color: typeKleur }}>
          {t(`type.${k.type}`, taal)}
        </span>
      </div>

      {/* art-zone: procedurele SVG-art (of statische webp via de inruil-haak) */}
      <div
        className="mx-2 mt-2 h-16 overflow-hidden rounded-md"
        style={{ border: `1px solid ${accent}33`, background: '#0a0a0f' }}
      >
        <KaartArt kaart={k} kleur={kleur} accent={accent} className="h-full w-full" />
      </div>

      {/* naam */}
      <div className="px-2 pt-2 text-[13px] font-semibold leading-tight" style={{ fontFamily: 'Cinzel, serif', color: '#ece6da' }}>
        {kaartNaam(k, taal)}
      </div>

      {/* tekst */}
      <div className="px-2 pb-2 pt-1 text-[10px] leading-snug text-slate-300">{kaartTekst(k, taal)}</div>

      {bevroren && (
        <div className="absolute inset-0 flex items-center justify-center bg-cyan-200/15 text-[11px] font-bold uppercase tracking-widest text-cyan-100 backdrop-blur-[1px]">
          {t('ui.bevroren', taal)}
        </div>
      )}
    </button>
  );
}
